import { AxiosError } from 'axios'
import { StatusCodes } from 'http-status-codes'

import type { ConstraintViolationListError } from '../../types/misc'

class ErrorTypeService {
  isApiError(error: unknown): error is AxiosError<{
    'hydra:title': string
    'hydra:description': string
  }> {
    if (!this.isAxiosError(error)) {
      return false
    }

    const responseData = error.response?.data

    return !!(
      responseData?.hasOwnProperty('hydra:title') &&
      responseData?.hasOwnProperty('hydra:description')
    )
  }

  isForbiddenError(error: unknown): boolean {
    return (
      this.isAxiosError(error) &&
      error.response?.status === StatusCodes.FORBIDDEN
    )
  }

  isBadRequestError(error: unknown): error is AxiosError {
    return (
      this.isAxiosError(error) &&
      error.response?.status === StatusCodes.BAD_REQUEST
    )
  }

  isUnprocessibleEntityError(error: unknown): error is AxiosError {
    return (
      this.isAxiosError(error) &&
      error.response?.status === StatusCodes.UNPROCESSABLE_ENTITY
    )
  }

  is404Error(error: unknown): boolean {
    return (
      this.isAxiosError(error) &&
      error.response?.status === StatusCodes.NOT_FOUND
    )
  }

  isAuthenticationError(error: unknown): boolean {
    if (!this.isAxiosError(error)) {
      return false
    }

    const responseBody = error.response?.data

    return (
      typeof responseBody === 'object' &&
      responseBody !== null &&
      'code' in responseBody &&
      responseBody.code === 'app:unauthenticated'
    )
  }

  isAxiosError(error: unknown): error is AxiosError {
    return error instanceof AxiosError
  }

  isConstraintViolationListError(
    error: unknown,
  ): error is ConstraintViolationListError {
    return (
      error instanceof AxiosError &&
      error.response?.status === StatusCodes.UNPROCESSABLE_ENTITY &&
      error.response.data['@type'] === 'ConstraintViolationList' &&
      Array.isArray(error.response.data['violations'])
    )
  }

  isNetworkError(error: unknown): error is AxiosError {
    return this.isAxiosError(error) && !error.response
  }
}

export const errorTypeService = new ErrorTypeService()
